import { useCallback, useEffect, useRef, useState } from 'react';
import {
  freeBallOffered,
  parseMatchEvent,
  type MatchEvent,
  type NewMatchEvent,
  type ScoreState,
} from '@aiscoreboard/contracts';
import {
  ApiError, MatchRequestTimeoutError, awardFoul, clientId, connect, endFrame, endMatch,
  getMatch, isTransientMatchFailure, restoreAfterFoul, nextFrame, sendEvent, undo,
} from './api';
import { useT } from './i18n';
import {
  applyOptimisticEvent,
  enqueueMatchWrite,
  latestMatchState,
  projectOptimisticState,
  rebaseScoringEvent,
} from './matchState';
import { MATCH_STORAGE_KEY } from './matchStorage';
export { loadStoredMatchId, clearStoredMatchId } from './matchStorage';

export interface Suggestion {
  id: string;
  event: MatchEvent;
}

interface MatchSession {
  matchId: string | null;
  pending: number;
  writeQueue: Promise<void>;
  confirmedState: ScoreState | null;
  visibleState: ScoreState | null;
  confirmations: Map<string, Set<(result: { state: ScoreState }) => void>>;
  optimisticEvents: Map<string, MatchEvent>;
}

function newSession(matchId: string | null): MatchSession {
  return {
    matchId, pending: 0, writeQueue: Promise.resolve(), confirmedState: null, visibleState: null,
    confirmations: new Map(), optimisticEvents: new Map(),
  };
}

/**
 * Mirrors confirmed server state, rejecting stale HTTP and socket updates.
 *
 * `remember` is what tells a device whose match this is. The scoreboard keeps
 * the id so a reload or a flat battery does not lose the match; a phone acting
 * as a remote does not, because the match belongs to the table it was scanned
 * at, and a remote that quietly adopted it would reopen a stranger's match the
 * next time that phone opened the app.
 */
export function useMatch(matchId: string | null, remember = true) {
  const t = useT();
  const [state, setState] = useState<ScoreState | null>(null);
  const [connected, setConnected] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  /**
   * A refusal the app expected, kept apart from a failure it did not.
   *
   * "Nothing to undo" is not an error — it is the correct answer to a
   * reasonable question, and a person will ask it several times a night by
   * tapping Undo once more than there were shots. Putting it in the same red
   * line as a dropped connection teaches players to ignore that line, which is
   * the one place a real problem has to appear.
   */
  const [notice, setNotice] = useState<string | null>(null);
  const [winnerRequest, setWinnerRequest] = useState<{ matchId: string; frameNumber: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [visibleState, setVisibleState] = useState<ScoreState | null>(null);
  const session = useRef<MatchSession>(newSession(null));
  const [slowSession, setSlowSession] = useState<MatchSession | null>(null);

  useEffect(() => {
    setWinnerRequest(current => current && (
      current.matchId !== state?.matchId || current.frameNumber !== state?.frameNumber
      || state?.status !== 'in_progress' || state.scores[0] !== state.scores[1]
    ) ? null : current);
  }, [state?.matchId, state?.frameNumber, state?.status, state?.scores[0], state?.scores[1]]);

  useEffect(() => {
    setSlowSession(null);
    if (!saving) return;
    const owner = session.current;
    // Fast writes should not flash a toast on every shot or turn switch.
    const timer = window.setTimeout(() => {
      if (session.current === owner && owner.pending > 0) setSlowSession(owner);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [saving, matchId]);

  const acceptState = useCallback((owner: MatchSession, next: ScoreState): void => {
    if (session.current !== owner) return;
    owner.confirmedState = latestMatchState(owner.confirmedState, next, owner.matchId);
    if (owner.confirmedState) {
      owner.visibleState = projectOptimisticState(owner.confirmedState, [...owner.optimisticEvents.values()]);
      setVisibleState(owner.visibleState);
      setState((current) => latestMatchState(current, owner.confirmedState!, owner.matchId));
    }
  }, []);

  useEffect(() => {
    const owner = newSession(matchId);
    session.current = owner;
    setVisibleState(null);
    setState(null);
    setSaving(false);
    setConnected(false);
    setSuggestions([]);
    setNotice(null);
    setWinnerRequest(null);
    setError(null);
    if (!matchId) return;
    if (remember) localStorage.setItem(MATCH_STORAGE_KEY, matchId);

    let cancelled = false;
    getMatch(matchId)
      .then((snapshot) => {
        if (!cancelled) acceptState(owner, snapshot.state);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });

    const dispose = connect(
      matchId,
      (message) => {
        if (cancelled) return;
        switch (message.type) {
          case 'snapshot':
            acceptState(owner, message.match.state);
            break;
          case 'event':
            if (message.event.matchId !== owner.matchId || message.state.matchId !== owner.matchId) break;
            if (message.event.clientId) owner.optimisticEvents.delete(message.event.clientId);
            acceptState(owner, message.state);
            if (message.event.clientId) {
              owner.confirmations.get(message.event.clientId)?.forEach(confirm => confirm({ state: message.state }));
            }
            break;
          case 'suggestion':
            setSuggestions((prev) => [...prev, { id: message.id, event: message.event }]);
            break;
          case 'error':
            setError(message.message);
            break;
        }
      },
      (value) => {
        if (!cancelled) setConnected(value);
      },
    );

    return () => {
      cancelled = true;
      owner.confirmations.clear();
      session.current = newSession(null);
      dispose();
    };
  }, [matchId, remember, acceptState]);

  const run = useCallback(
    async (
      call: (id: string) => Promise<{ state: ScoreState }>,
      undoRefusal = false,
      requestId?: string,
      optimisticEvent?: NewMatchEvent,
    ) => {
      const owner = session.current;
      if (!matchId || owner.matchId !== matchId) return false;
      const optimisticKey = optimisticEvent ? requestId ?? clientId() : null;
      if (optimisticEvent && owner.confirmedState?.matchId === matchId && optimisticKey) {
        const projectedEvent = parseMatchEvent({
          ...optimisticEvent,
          seq: owner.confirmedState.lastSeq + owner.optimisticEvents.size + 1,
          matchId,
          at: new Date().toISOString(),
        });
        const projectedBase = owner.visibleState ?? owner.confirmedState;
        owner.visibleState = applyOptimisticEvent(projectedBase, projectedEvent, projectedBase.lastSeq + owner.optimisticEvents.size + 1);
        owner.optimisticEvents.set(optimisticKey, projectedEvent);
        setVisibleState(owner.visibleState);
      }
      if (owner.pending === 0) setSaving(true);
      owner.pending += 1;
      setError(null);
      setNotice(null);
      let syncError: unknown;
      let confirm: ((result: { state: ScoreState }) => void) | undefined;
      const confirmation = requestId ? new Promise<{ state: ScoreState }>(resolve => {
        confirm = resolve;
        const callbacks = owner.confirmations.get(requestId) ?? new Set();
        callbacks.add(resolve);
        owner.confirmations.set(requestId, callbacks);
      }) : undefined;
      try {
        // The exact persisted event is also an acknowledgement; unrelated
        // snapshots or another client's score must never complete this write.
        const result = await enqueueMatchWrite(
          owner,
          () => {
            if (session.current !== owner) {
              return Promise.reject(new Error('Match session changed before the write was sent'));
            }
            return call(matchId).catch(async writeError => {
              if (session.current === owner) {
                try {
                  const snapshot = await getMatch(matchId);
                  if (session.current === owner) acceptState(owner, snapshot.state);
                } catch (error) {
                  syncError = error;
                }
              }
              throw writeError;
            });
          },
          confirmation ? request => Promise.race([request, confirmation]) : undefined,
        );
        if (session.current !== owner) return false;
        if (optimisticKey) owner.optimisticEvents.delete(optimisticKey);
        acceptState(owner, result.state);
        setError(null);
        setNotice(null);
        return true;
      } catch (e) {
        if (session.current !== owner) return false;
        if (undoRefusal && e instanceof ApiError && e.status === 409 && e.message === 'nothing to undo') {
          setNotice(t('match.nothingToUndo'));
        } else if (e instanceof ApiError && e.status === 409 && e.message === 'frame scores are tied') {
          if (state?.frameNumber !== undefined) {
            setWinnerRequest({ matchId, frameNumber: state.frameNumber });
          }
        } else {
          setError(e instanceof MatchRequestTimeoutError
            ? t('match.requestUnconfirmed')
            : isTransientMatchFailure(e) ? t('match.connectionUnconfirmed')
              : e instanceof Error ? e.message : String(e));
        }
        if (syncError && session.current === owner) {
          setError(`${t('match.requestUnconfirmed')}: ${syncError instanceof Error ? syncError.message : String(syncError)}`);
        }
        return false;
      } finally {
        if (optimisticKey && owner.optimisticEvents.delete(optimisticKey)) {
          owner.visibleState = projectOptimisticState(
            owner.confirmedState,
            [...owner.optimisticEvents.values()],
          );
          setVisibleState(owner.visibleState);
        }
        if (requestId && confirm) {
          const callbacks = owner.confirmations.get(requestId);
          callbacks?.delete(confirm);
          if (callbacks?.size === 0) owner.confirmations.delete(requestId);
        }
        owner.pending -= 1;
        if (session.current === owner) setSaving(owner.pending > 0);
      }
    },
    [matchId, acceptState, t, state?.frameNumber, state?.matchId, state?.lastSeq],
  );

  const dispatch = useCallback(
    async (event: NewMatchEvent) => {
      const requestId = event.clientId ?? clientId();
      const projected = session.current.visibleState;
      const nextEvent = { ...event, clientId: requestId };
      const rebasedEvent = projected?.matchId === matchId ? rebaseScoringEvent(nextEvent, projected) : nextEvent;
      const optimisticEvent = rebasedEvent;
      return run((id) => sendEvent(id, optimisticEvent), false, requestId, optimisticEvent);
    },
    [run],
  );

  /**
   * Undo, including the case where there is nothing left to undo.
   *
   * The server answers that with `409`, which arrived here as a thrown error
   * and went straight onto the board as `409 Conflict {"error":"nothing to
   * undo"}`. It is not a fault in the app and it is not something the player
   * can fix, so it is answered in their own language and in the calm colour,
   * and it clears itself rather than sitting there after the next shot.
   */
  const undoLast = useCallback(async () => {
    const requestId = clientId();
    await run(id => undo(id, requestId), true, requestId);
  }, [run]);

  const recordFoul = useCallback(async (points: number) => {
    if (!state) return;
    if (state.mode === 'assisted') {
      await run(id => awardFoul(id, points, session.current.confirmedState?.lastSeq ?? state.lastSeq));
    } else {
      await dispatch({ type: 'shot.foul', source: 'manual', player: state.activePlayer,
        points, offenderPlaysAgain: false, freeBall: false });
    }
  }, [state, run, dispatch]);

  const returnToOffender = useCallback(async () => {
    if (!state || state.paused || !freeBallOffered(state)) return;
    if (state.mode === 'assisted') {
      await run(id => restoreAfterFoul(id, session.current.confirmedState?.lastSeq ?? state.lastSeq));
      return;
    }
    await dispatch({
      type: 'turn.switched',
      source: 'manual',
      player: state.activePlayer === 0 ? 1 : 0,
    });
  }, [state, dispatch, run]);

  // Long enough to read twice, short enough that it is gone before anyone
  // wonders whether it is still true.
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 3000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  /** Explicit winners are only needed for referee concessions. */
  const finishFrame = useCallback(
    async (winner?: 0 | 1) => {
      if (session.current.pending > 0) return;
      const requestId = clientId();
      await run((id) => endFrame(id, winner, requestId), false, requestId);
    },
    [run],
  );

  const startNextFrame = useCallback(async () => {
    await run(nextFrame);
  }, [run]);

  const concedeMatch = useCallback(
    async (winner: 0 | 1) => {
      await run((id) => endMatch(id, winner));
    },
    [run],
  );

  const resolveSuggestion = useCallback(
    async (suggestion: Suggestion, accept: boolean) => {
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
      if (!accept) return;
      const { seq: _seq, at: _at, matchId: _matchId, ...rest } = suggestion.event;
      // Re-submitted as `manual`: a human confirmed it, so it now carries
      // human authority and will never be overwritten by the recogniser.
      await dispatch({ ...rest, source: 'manual' } as NewMatchEvent);
    },
    [dispatch],
  );

  return {
    refresh: async () => { await run(getMatch); },
    state: visibleState?.matchId === matchId ? visibleState : null,
    saving,
    savingSlow: saving && slowSession === session.current,
    connected,
    suggestions,
    error,
    notice,
    dispatch,
    undoLast,
    returnToOffender,
    recordFoul,
    resolveSuggestion,
    finishFrame,
    frameWinnerRequired: winnerRequest?.matchId === state?.matchId
      && winnerRequest?.frameNumber === state?.frameNumber && state?.status === 'in_progress',
    cancelFrameWinner: () => setWinnerRequest(null),
    startNextFrame,
    concedeMatch,
  };
}
